import { useTheme } from "@/src/styles/theme-provider";
import {
  passwordStrengthScore,
  validatePassword,
} from "@/src/utils/form-validators";
import { TickCircle } from "iconsax-react-nativejs";
import { cssInterop } from "nativewind";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";

const StyledTickCircle = cssInterop(TickCircle, {
  className: {
    target: "style",
    nativeStyleToProp: {
      color: true,
    },
  },
});

export default function PasswordStrengthValidator({
  password,
}: {
  password: string;
}) {
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  const isEmpty = !password || password.length === 0;
  const rules = validatePassword(password);
  const passedRules = passwordStrengthScore(password);

  const strengthText = [
    t("LABEL_WEAK"),
    t("LABEL_OKAY"),
    t("LABEL_GOOD"),
    t("LABEL_STRONG"),
  ];

  // Bar fill colors — semantic strength indicators, not UI chrome,
  // so kept as Tailwind color literals rather than mapped to theme tokens.
  const colorClasses = [
    "bg-crimson",
    "bg-yellow-400",
    "bg-cyan-300",
    "bg-jade",
  ];

  const textClasses = [
    "text-crimson",
    "text-yellow-400",
    "text-cyan-300",
    "text-jade",
  ];

  const strengthIndex = isEmpty
    ? -1
    : Math.max(0, Math.min(passedRules, strengthText.length - 1));

  return (
    <View className="mt-2">
      <View className="flex-row gap-2 mb-2">
        {[0, 1, 2, 3].map((i) => (
          <View
            key={i}
            className={`flex-1 h-1 rounded-full ${
              !isEmpty && strengthIndex >= 0 && i <= strengthIndex
                ? colorClasses[strengthIndex]
                : "bg-ash-3"
            }`}
          />
        ))}
      </View>

      {!isEmpty && strengthIndex >= 0 && (
        <Text
          className={`text-xs font-medium mb-1 ${textClasses[strengthIndex]}`}
        >
          {strengthText[strengthIndex]}
        </Text>
      )}

      {!isEmpty && (
        <View className="gap-1 mb-4">
          <Rule
            text={t("HINT_CHARACTER_EIGHT")}
            valid={rules.length}
            themeVars={themeVars}
          />
          <Rule
            text={t("HINT_CAP_LETTER_ONE")}
            valid={rules.upper}
            themeVars={themeVars}
          />
          <Rule
            text={t("HINT_ONE_NUMBER")}
            valid={rules.number}
            themeVars={themeVars}
          />
          <Rule
            text={t("HINT_SPECIAL_CHARACTER")}
            valid={rules.special}
            themeVars={themeVars}
          />
        </View>
      )}
    </View>
  );
}

function Rule({
  text,
  valid,
  themeVars,
}: {
  text: string;
  valid: boolean;
  themeVars: Record<string, string>;
}) {
  return (
    <View className="flex-row items-center gap-1">
      {valid ? (
        <StyledTickCircle size={14} className="text-jade" variant="Bold" />
      ) : (
        <StyledTickCircle
          size={14}
          className="text-crimson"
          variant="Outline"
        />
      )}
      <Text className={`text-xs ${valid ? "text-jade" : "text-crimson"}`}>
        {text}
      </Text>
    </View>
  );
}
